/**
 * CANONICAL axios instance factory — vendored copy of
 * `software-architect-ai/templates/api-axios-instance.ts` (see that repo's
 * `http-client-strategy.md`). It is vendored because the standard lives in a docs
 * repo, not an npm package.
 *
 * EXTEND THIS VIA `createApiClient(config)`, DON'T FORK IT. `src/api/client.ts` is the
 * only caller: it supplies the repo's base URL, in-memory token store, path predicates
 * and `onSessionExpired`. The four interceptors (base+headers → bearer → csrf →
 * silent-refresh+retry → error-normalize) stay centralized here.
 *
 * DEVIATIONS from the upstream template — two corrections inside the silent-refresh
 * helper only; interceptor structure, order and logic are otherwise verbatim. Both are
 * flagged in the delivery doc for the Architect to fold back upstream:
 *
 *  1. **Refresh-request recursion guard.** Upstream sets an `X-Refresh: '1'` header on the
 *     refresh POST but never reads it, so a refresh that itself answers `401` (the
 *     ordinary "no live session" boot case) re-enters `runRefresh()`, receives the
 *     still-pending single-in-flight promise, and awaits itself — a permanent deadlock
 *     that hangs the boot guard instead of redirecting to login. The guard is now
 *     implemented, which is what the strategy doc's "bounded: never more than one refresh
 *     + one retry per original call" requires. It marks the request via a **config flag**
 *     rather than the wire header, so it adds nothing to the request that saas-auth's CORS
 *     `allowedHeaders` would have to permit.
 *  2. **`expiresIn` support.** Upstream reads only `expiresAt` from the refresh body.
 *     saas-auth's `AuthTokenResponse` ships `expiresIn` (seconds) — see the genesis
 *     handoff §5 — so the refresh body's expiry was silently dropped. Both fields are
 *     now accepted and normalized to epoch ms.
 *
 * Plus compiler-only adaptations to this repo's stricter tsconfig (`noImplicitOverride`,
 * `exactOptionalPropertyTypes`): an `override` modifier, explicit `| undefined` on optional
 * members, and an `undefined` check before the `baseURL` assignment. No runtime behaviour
 * differs from upstream.
 *
 * Pin: axios ^1.16.0. Tokens are IN MEMORY ONLY (no localStorage/sessionStorage).
 */
import axios, {
  type AxiosInstance,
  type AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';

/* ---------------------------------------------------------------- error model */

/** Extend this union per repo with the backend's known codes. Unknown -> 'UNKNOWN'. */
export type ErrorCode =
  'SESSION_EXPIRED' | 'CSRF_VALIDATION_FAILED' | 'NETWORK_ERROR' | 'UNKNOWN' | (string & {}); // allow repo-specific codes without losing autocomplete

export class ApiError extends Error {
  // `override` + the explicit `| undefined`s are this repo's strict-mode settings
  // (`noImplicitOverride`, `exactOptionalPropertyTypes`) applied to the template's types.
  // Compiler-only — the runtime shape is byte-for-byte the canonical one.
  override readonly name = 'ApiError';
  constructor(
    readonly code: ErrorCode,
    readonly status: number,
    readonly requestId?: string | undefined,
    readonly details: { message?: string | undefined; raw?: unknown } = {},
  ) {
    super(`API error: ${code}`);
  }
}

interface BackendEnvelope {
  success?: false;
  error?: { code?: string; message?: string };
}

/* --------------------------------------------------------------------- config */

export interface ApiClientConfig {
  /** Single base URL, OR a per-path resolver for multi-base surfaces (admin vs auth). */
  baseUrl?: string;
  resolveBaseUrl?: (path: string) => string;

  /** In-memory token store. The factory NEVER touches storage directly. */
  tokens: {
    getAccess: () => string | null;
    setAccess: (token: string | null, expiresAt?: number | null) => void;
    getCsrf: () => string | null;
    setCsrf: (token: string | null) => void;
  };

  /** Attach Authorization: Bearer on these paths. */
  bearer: { protected: (path: string) => boolean };

  /** CSRF policy. */
  csrf: {
    protected: (path: string, method: string) => boolean;
    /** 'cookie-echo' (same-origin auth surface) | 'store-token' (cross-origin surface). */
    mode: (path: string) => 'cookie-echo' | 'store-token';
    cookieName?: string; // default 'journix_csrf'
    fetchPath?: string; // default '/api/auth/csrf' (cookie-echo fallback)
  };

  /** Silent refresh endpoint (flat body: { accessToken, expiresIn?/expiresAt?, csrfToken? }). */
  refresh: { path: string };

  /** Called when refresh fails — repo signs out / redirects to login. */
  onSessionExpired: () => void;

  /** OPTIONAL. Omit to rely on server-derived tenancy (the default). */
  getTenantContext?: () => { tenantId?: string } | null;

  /** Generate a request id (uuid). Repo supplies its own. */
  generateRequestId?: () => string;
}

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** Private config flag marking a request AS a refresh (deviation 1) — never sent on the wire. */
const REFRESH_FLAG = '__isRefresh';

interface RefreshMarked {
  [REFRESH_FLAG]?: boolean;
}

/**
 * Axios config that marks a call as a refresh, keeping it out of the silent-refresh retry
 * path. Exported so a repo's own explicit refresh (e.g. the boot refresh) gets the same
 * guarantee as the factory's internal one.
 */
export function refreshRequestConfig(): AxiosRequestConfig {
  return { [REFRESH_FLAG]: true } as AxiosRequestConfig;
}

/* --------------------------------------------------------------------- factory */

export function createApiClient(config: ApiClientConfig): AxiosInstance {
  const instance = axios.create({ withCredentials: true });
  const reqId = config.generateRequestId ?? (() => crypto.randomUUID());
  const cookieName = config.csrf.cookieName ?? 'journix_csrf';
  const csrfFetchPath = config.csrf.fetchPath ?? '/api/auth/csrf';

  let refreshInFlight: Promise<boolean> | null = null;
  let csrfFetchInFlight: Promise<string | null> | null = null;

  /* [1+2+3] one request interceptor, deterministic order: base+headers -> bearer -> csrf */
  instance.interceptors.request.use(async (req) => {
    const path = pathOf(req);
    const method = (req.method ?? 'get').toUpperCase();

    // [1] base URL + standard headers + optional tenant context
    const resolvedBase = config.resolveBaseUrl ? config.resolveBaseUrl(path) : config.baseUrl;
    if (resolvedBase !== undefined) req.baseURL = resolvedBase;
    req.headers.set('Accept', 'application/json');
    req.headers.set('X-Request-ID', reqId());
    const tenant = config.getTenantContext?.();
    if (tenant?.tenantId) req.headers.set('X-Tenant-ID', tenant.tenantId);

    // [2] bearer
    if (config.bearer.protected(path)) {
      const token = config.tokens.getAccess();
      if (token) req.headers.set('Authorization', `Bearer ${token}`);
    }

    // [3] csrf (two modes)
    if (config.csrf.protected(path, method) && MUTATING.has(method)) {
      if (config.csrf.mode(path) === 'cookie-echo') {
        const token = readCookie(cookieName) ?? (await fetchCsrf());
        if (token) {
          req.headers.set('X-CSRF-Token', token);
          if (isPlainObject(req.data)) req.data = { ...req.data, csrfToken: token }; // body echo
        }
      } else {
        const token = config.tokens.getCsrf(); // store-token (cross-origin): header only
        if (token) req.headers.set('X-CSRF-Token', token);
      }
    }
    return req;
  });

  /* [4a + 4b] one response interceptor: try refresh+retry, else normalize-and-throw */
  instance.interceptors.response.use(
    (res) => res,
    async (error: AxiosError<BackendEnvelope>) => {
      const original = error.config as
        (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
      const status = error.response?.status ?? 0;
      const backendCode = error.response?.data?.error?.code;
      const path = original ? pathOf(original) : '';
      const method = (original?.method ?? 'get').toUpperCase();

      // [4a] silent refresh + single retry (bounded by _retry, and by the refresh
      // marker so the refresh call itself never re-enters runRefresh — deviation 1).
      const refreshable =
        !!original &&
        !original._retry &&
        !isRefreshRequest(original) &&
        (status === 401 ||
          (status === 403 &&
            backendCode === 'INVALID_CSRF' &&
            config.csrf.mode(path) === 'store-token' &&
            MUTATING.has(method)));
      if (refreshable) {
        original._retry = true;
        const ok = await runRefresh();
        if (ok) return instance(original);
        config.onSessionExpired();
        throw new ApiError('SESSION_EXPIRED', 401, idOf(error));
      }

      // [4b] error normalization
      throw normalize(error, status, backendCode);
    },
  );

  /* ---- helpers ---- */

  async function runRefresh(): Promise<boolean> {
    if (refreshInFlight) return refreshInFlight;
    refreshInFlight = (async () => {
      try {
        const { data } = await instance.post<{
          accessToken?: string;
          expiresIn?: number | null;
          expiresAt?: number | null;
          csrfToken?: string | null;
        }>(config.refresh.path, {}, refreshRequestConfig());
        if (!data?.accessToken) return false;
        // Deviation 2: saas-auth ships `expiresIn` (seconds); accept either field.
        config.tokens.setAccess(data.accessToken, expiryFrom(data));
        config.tokens.setCsrf(data.csrfToken ?? null); // authoritative for store-token surfaces
        return true;
      } catch {
        return false;
      }
    })().finally(() => {
      refreshInFlight = null;
    });
    return refreshInFlight;
  }

  async function fetchCsrf(): Promise<string | null> {
    if (csrfFetchInFlight) return csrfFetchInFlight;
    csrfFetchInFlight = instance
      .get<{ csrfToken?: string }>(csrfFetchPath)
      .then((r) => r.data?.csrfToken ?? null)
      .catch(() => null)
      .finally(() => {
        csrfFetchInFlight = null;
      });
    return csrfFetchInFlight;
  }

  return instance;
}

/* ----------------------------------------------------------- module helpers */

/** Epoch-ms expiry from either `expiresAt` (epoch ms) or `expiresIn` (seconds). */
export function expiryFrom(body: {
  expiresIn?: number | null;
  expiresAt?: number | null;
}): number | null {
  if (typeof body.expiresAt === 'number') return body.expiresAt;
  if (typeof body.expiresIn === 'number' && body.expiresIn > 0) {
    return Date.now() + body.expiresIn * 1000;
  }
  return null;
}

function isRefreshRequest(req: InternalAxiosRequestConfig): boolean {
  return (req as InternalAxiosRequestConfig & RefreshMarked)[REFRESH_FLAG] === true;
}

function normalize(
  error: AxiosError<BackendEnvelope>,
  status: number,
  backendCode?: string,
): ApiError {
  if (!error.response) {
    return new ApiError('NETWORK_ERROR', 0, idOf(error), { message: error.message });
  }
  const code: ErrorCode = backendCode ?? 'UNKNOWN';
  return new ApiError(code, status, idOf(error), {
    message: error.response.data?.error?.message,
    raw: backendCode ? undefined : error.response.data,
  });
}

function pathOf(req: { url?: string; baseURL?: string }): string {
  const u = req.url ?? '';
  if (u.startsWith('http')) {
    try {
      return new URL(u).pathname;
    } catch {
      return u;
    }
  }
  return u;
}

function idOf(error: AxiosError): string | undefined {
  const h = error.config?.headers?.['X-Request-ID'];
  return typeof h === 'string' ? h : undefined;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const esc = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${esc}=([^;]+)`));
  return m?.[1] ? decodeURIComponent(m[1]) : null;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}
